<template>
  <div class="stock-kline">
    <div class="kline-header">
      <h3>{{ currentStockName || stockCode }} 5日K线</h3>
      <div class="controls">
        <el-select
          v-model="selectedStockCode"
          placeholder="请选择股票"
          filterable
          @change="handleStockChange"
          class="stock-select"
          v-loading="stockListLoading"
        >
          <el-option
            v-for="item in stockList"
            :key="item.stock_code"
            :label="`${item.stock_name} (${item.stock_code})`"
            :value="item.stock_code"
          >
          </el-option>
        </el-select>
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="开始日期"
          value-format="yyyy-MM-dd"
          @change="fetchData"
        />
        <span>至</span>
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="fetchData"
        />
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
    </div>
    <div ref="klineChart" class="kline-chart" v-loading="loading"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'StockKLine5Day',
  props: {
    stockCode: {
      type: String,
      default: 'sz.002602'
    }
  },
  data() {
    return {
      chart: null,
      loading: false,
      startDate: "2020-01-01",
      endDate: this.formatDate(new Date()),
      klineData: [],
      stockList: [],
      stockListLoading: false,
      selectedStockCode: '',
      currentStockName: ''
    }
  },
  watch: {
    // 监听路由参数变化
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          // 更新股票名称
          const selectedStock = this.stockList.find(item => item.stock_code === newCode)
          if (selectedStock) {
            this.currentStockName = selectedStock.stock_name
          }
          // 如果代码变化了，更新并获取数据
          if (newCode !== this.selectedStockCode) {
            this.selectedStockCode = newCode
            this.fetchStockList()
            this.fetchDataWithCode(newCode)
          }
        }
      }
    }
  },
  mounted() {
    this.initChart()
    if (!this.$route.query.code) {
      this.selectedStockCode = this.stockCode
      this.fetchStockList()
      this.fetchData()
    }

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 初始化图表
    initChart() {
      this.chart = echarts.init(this.$refs.klineChart)
      const option = this.getChartOption()
      this.chart.setOption(option)
    },

    // 获取股票列表
    async fetchStockList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success' && response.data.data) {
          this.stockList = response.data.data
        } else {
          this.stockList = []
        }
      } catch (error) {
        console.error('获取股票列表失败:', error)
        this.$message.error('获取股票列表失败')
        this.stockList = []
      } finally {
        this.stockListLoading = false
      }
    },

    // 选择股票变化
    handleStockChange(code) {
      // 更新URL参数，watch会处理数据获取
      this.$router.push({ query: { code } })
    },

    // 使用指定股票代码获取数据
    async fetchDataWithCode(code) {
      this.loading = true
      try {
        const params = {
          stock_code: code,
          start_date: this.startDate,
          end_date: this.endDate,
          frequency: '5'
        }
        console.log('请求参数:', params)

        const response = await axios.get('/stock/history/k', { params })

        if (response.data.status === 'success') {
          this.klineData = this.parseData(response.data.data)
          this.updateChart()
        } else {
          this.$message.error('返回状态异常: ' + (response.data.message || '未知错误'))
        }
      } catch (error) {
        console.error('获取K线数据失败:', error)
        this.$message.error('获取K线数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    // 获取K线数据
    async fetchData() {
      this.fetchDataWithCode(this.selectedStockCode)
    },

    // 解析后端返回的数据
    parseData(data) {
      return data.map(item => ({
        date: item.date,
        code: item.code,
        open: parseFloat(item.open),
        high: parseFloat(item.high),
        low: parseFloat(item.low),
        close: parseFloat(item.close),
        volume: parseInt(item.volume),
        amount: parseFloat(item.amount),
        adjustFlag: item.adjust_flag || item.adjustFlag
      })).sort((a, b) => new Date(a.date) - new Date(b.date))
    },

    // 更新图表
    updateChart() {
      if (!this.chart || this.klineData.length === 0) return

      const dates = this.klineData.map(item => this.formatChartDate(item.date))
      const candlestickData = this.klineData.map(item => [
        item.open,
        item.close,
        item.low,
        item.high
      ])
      const volumes = this.klineData.map(item => ({
        value: item.volume,
        color: item.close >= item.open ? '#ef5350' : '#26a69a'
      }))

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex
            const data = this.klineData[dataIndex]
            return `
              <div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${data.date}</div>
                <div>开盘: ${data.open.toFixed(2)}</div>
                <div>收盘: ${data.close.toFixed(2)}</div>
                <div>最高: ${data.high.toFixed(2)}</div>
                <div>最低: ${data.low.toFixed(2)}</div>
                <div>成交量: ${(data.volume / 10000).toFixed(2)}万手</div>
                <div>成交额: ${(data.amount / 100000000).toFixed(2)}亿</div>
              </div>
            `
          }
        },
        legend: {
          data: ['K线', 'MA5', 'MA10', 'MA20', '成交量'],
          top: 10
        },
        grid: [
          {
            left: '10%',
            right: '10%',
            top: '15%',
            height: '55%'
          },
          {
            left: '10%',
            right: '10%',
            top: '75%',
            height: '15%'
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: dates,
            boundaryGap: false,
            axisLine: { onZero: false },
            splitLine: { show: false },
            min: 'dataMin',
            max: 'dataMax',
            axisLabel: {
              formatter: (value) => value
            }
          },
          {
            type: 'category',
            gridIndex: 1,
            data: dates,
            boundaryGap: false,
            axisLine: { onZero: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            min: 'dataMin',
            max: 'dataMax'
          }
        ],
        yAxis: [
          {
            scale: true,
            splitLine: { show: false },
            splitArea: { show: false },
            axisLine: { show: true },
            axisLabel: { show: true }
          },
          {
            scale: true,
            gridIndex: 1,
            splitNumber: 2,
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: 0,
            end: 100
          },
          {
            show: true,
            xAxisIndex: [0, 1],
            type: 'slider',
            top: '92%',
            start: 0,
            end: 100
          }
        ],
        series: [
          {
            name: 'K线',
            type: 'candlestick',
            data: candlestickData,
            itemStyle: {
              color: '#ef5350',
              color0: '#26a69a',
              borderColor: '#ef5350',
              borderColor0: '#26a69a'
            }
          },
          {
            name: 'MA5',
            type: 'line',
            data: this.calculateMA(5),
            smooth: true,
            lineStyle: {
              opacity: 0.8,
              width: 1
            },
            itemStyle: {
              color: '#f59e0b'
            }
          },
          {
            name: 'MA10',
            type: 'line',
            data: this.calculateMA(10),
            smooth: true,
            lineStyle: {
              opacity: 0.8,
              width: 1
            },
            itemStyle: {
              color: '#3b82f6'
            }
          },
          {
            name: 'MA20',
            type: 'line',
            data: this.calculateMA(20),
            smooth: true,
            lineStyle: {
              opacity: 0.8,
              width: 1
            },
            itemStyle: {
              color: '#8b5cf6'
            }
          },
          {
            name: '成交量',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: volumes,
            itemStyle: {
              color: (params) => params.color
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    // 计算移动平均线
    calculateMA(dayCount) {
      const result = []
      for (let i = 0, len = this.klineData.length; i < len; i++) {
        if (i < dayCount - 1) {
          result.push('-')
          continue
        }
        let sum = 0
        for (let j = 0; j < dayCount; j++) {
          sum += this.klineData[i - j].close
        }
        result.push((sum / dayCount).toFixed(2))
      }
      return result
    },

    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 格式化图表显示的日期
    formatChartDate(dateStr) {
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    getChartOption() {
      return {
        title: {
          text: '加载中...',
          left: 'center'
        }
      }
    }
  }
}
</script>


<style scoped>
.stock-kline {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.kline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.kline-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stock-select {
  width: 250px;
}

.stock-select :deep(.el-input__inner),
.controls :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stock-select :deep(.el-input__inner):hover,
.controls :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15);
}

.stock-select :deep(.el-input__inner):focus,
.controls :deep(.el-date-picker .el-input__inner):focus {
  border-color: #6366f1;
  box-shadow: 0 0 16px rgba(99, 102, 241, 0.25);
}

.controls :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6);
}

.controls span {
  color: #64748b;
  font-size: 14px;
}

.controls :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.controls :deep(.el-button--primary):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.45);
}

.controls :deep(.el-button--primary):active {
  transform: translateY(0);
}

.kline-chart {
  width: 100%;
  height: 600px;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px;
}
</style>
